import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CartItemCounter from "../CartItemCounter/CartItemCounter";
import { INVENTORY_CLASSIFICATION } from "../../../helpers/const";
import PlaceholderIcon from "../Placeholder/Placeholder";
import {
  convertImageNameToWeb,
  formatMoney,
  getLineTotal,
  getTotalAvailableQty,
  willBackorder,
} from "../../../helpers/helperFunctions";
import ItemCounter from "../ItemCounter/ItemCounter";
import { addCartItem } from "../../../store/slices/CartSlice/CartSlice";
import WhatDoesThisFit from "../WhatDoesThisFit/WhatDoesThisFit";
import BackorderConfirmModal from "../BackorderConfirmModal/BackorderConfirmModal";
import { apiGet } from "../../../api/apiGet";

export default function ProductListRow({
  product,
  cartItem,
  idx,
  onSearchProductCode,
}) {
  const dispatch = useDispatch();
  const [cartQty, setCartQty] = useState(cartItem?.qty || 0);
  const [desiredQty, setDesiredQty] = useState(1);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showBackorderConfirm, setShowBackorderConfirm] = useState(false);
  const [alternative, setAlternative] = useState(null);

  // Update local cart quantity when cartItem changes to trigger re-renders
  useEffect(() => {
    setCartQty(cartItem?.quantity || 0);
  }, [cartItem]);

  const { updateCarts } = useSelector((state) => state.admin);

  // Out of stock -- look up an in-stock vendor-category alternative
  useEffect(() => {
    const available = getTotalAvailableQty(product);
    const outOfStock = available !== undefined && available - cartQty <= 0;

    if (!outOfStock) {
      setAlternative(null);
      return;
    }

    let cancelled = false;
    apiGet
      .getAlternativeProduct({ product_code: product.product_code, qty: 1 })
      .then((response) => {
        if (!cancelled) setAlternative(response.data || null);
      })
      .catch(() => {
        if (!cancelled) setAlternative(null);
      });

    return () => {
      cancelled = true;
    };
  }, [product, cartQty]);

  const handleSearchAlternative = () => {
    if (!alternative || !onSearchProductCode) return;
    onSearchProductCode(alternative.product_code);
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        setShowImageModal(false);
      }
    };

    if (showImageModal) {
      document.addEventListener("keydown", handleEsc, false);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEsc, false);
      document.body.style.overflow = "unset";
    };
  }, [showImageModal]);

  const dispatchAddCartItem = () => {
    dispatch(
      addCartItem({
        product_code: product.product_code,
        quantity: desiredQty,
        last_price: product.price,
        tariff_amount: product.tariff_amount,
        product: {
          shipping_weight: product.shipping_weight,
          description: product.description,
        },
      }),
    );
  };

  const handleAddCartItem = () => {
    if (willBackorder(product, cartQty, desiredQty)) {
      setShowBackorderConfirm(true);
      return;
    }
    dispatchAddCartItem();
  };

  const handleConfirmBackorder = () => {
    setShowBackorderConfirm(false);
    dispatchAddCartItem();
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    setShowImageModal(true);
  };

  const incrementDesiredQty = () => setDesiredQty((qty) => (qty || 0) + 1);

  const decrementDesiredQty = () =>
    setDesiredQty((qty) => Math.max(1, (qty || 0) - 1));

  const handleDesiredQtyChange = (event) => {
    const newValue = event.target.value;
    if (newValue === "") {
      setDesiredQty("");
      return;
    }
    if (/^[0-9]+$/.test(newValue)) {
      setDesiredQty(parseInt(newValue, 10));
    }
  };

  const handleDesiredQtyBlur = () => {
    if (desiredQty === "" || desiredQty < 1) setDesiredQty(1);
  };

  const handleModalClick = (e) => {
    // Close modal if clicking on the backdrop (not the image itself)
    if (e.target === e.currentTarget) {
      setShowImageModal(false);
    }
  };

  // Helper function to get color class based on inventory level
  // Uses acin for calculations, not the display qty
  const getInventoryColorClass = (acin) => {
    let act_qty = acin - cartQty;
    if (act_qty <= 0) {
      return INVENTORY_CLASSIFICATION.OUT_OF_STOCK.bgColorClass;
    } else if (act_qty < 10) {
      return INVENTORY_CLASSIFICATION.LOW_STOCK.bgColorClass;
    } else {
      return INVENTORY_CLASSIFICATION.IN_STOCK.bgColorClass;
    }
  };

  // Helper function to get inventory status text
  // Uses acin for calculations, not the display qty
  const getInventoryStatusText = (acin) => {
    let act_qty = acin - cartQty;
    if (act_qty <= 0) {
      return INVENTORY_CLASSIFICATION.OUT_OF_STOCK.label;
    } else if (act_qty < 10) {
      return INVENTORY_CLASSIFICATION.LOW_STOCK.label;
    } else {
      return INVENTORY_CLASSIFICATION.IN_STOCK.label;
    }
  };
  return (
    <>
      <tr
        key={idx}
        className="even:bg-gray-50 flex flex-col lg:table-row w-full lg:w-auto border-b border-gray-200"
      >
        {/* Combined Image and Product Information Column */}
        <td className="px-3 text-sm text-gray-700 flex lg:py-2">
          {/* Image on the left */}
          <div className="w-16 h-16 flex items-center justify-center mr-3 flex-shrink-0">
            {product.image_name ? (
              <img
                alt={product.image_name}
                src={
                  process.env.REACT_APP_IMAGE_URL +
                  convertImageNameToWeb(product.image_name)
                }
                className="max-w-full max-h-full object-contain cursor-pointer hover:scale-110 transition-transform duration-200"
                onClick={handleImageClick}
              />
            ) : (
              <PlaceholderIcon />
            )}
          </div>

          {/* Product information on the right */}
          <div className="flex flex-col flex-grow">
            <span className="font-bold flex items-center gap-2">
              {product.description}
              {/* Unified inventory display - prioritize qty_dict, fallback to qty_available */}
              {product.qty_dict ? (
                <div className="flex gap-2 items-center flex-wrap">
                  {Object.entries(product.qty_dict).map(([warehouse, qty]) => {
                    const incomingQty =
                      product.incoming_transfers?.[warehouse] || 0;
                    // Use acin for calculations and colors, but display qty_dict
                    const acinQty = product.acin?.[warehouse] ?? qty;
                    const showPlus = acinQty > qty;
                    return (
                      <div
                        key={warehouse}
                        className="flex items-center gap-1 bg-white bg-opacity-80 px-1 py-0.5 rounded text-gray-800"
                      >
                        <span className="text-xs font-normal">
                          {warehouse}:
                        </span>
                        <div
                          className={`w-3 h-3 rounded-sm ${getInventoryColorClass(acinQty)} ${acinQty > 0 && acinQty <= 5 ? "animate-pulse" : ""}`}
                          title={`${warehouse}: ${getInventoryStatusText(acinQty)}`}
                        ></div>
                        <span className="text-xs font-medium">
                          {qty}
                          {showPlus ? "+" : ""}
                        </span>
                        {incomingQty > 0 && (
                          <>
                            <span className="text-xs text-gray-400">+</span>
                            <div
                              className={`w-3 h-3 rounded-sm ${INVENTORY_CLASSIFICATION.TRANSFER_INCOMING.bgColorClass}`}
                              title={`${warehouse}: ${incomingQty} ${INVENTORY_CLASSIFICATION.TRANSFER_INCOMING.label}`}
                            ></div>
                            <span
                              className={`text-xs font-medium ${INVENTORY_CLASSIFICATION.TRANSFER_INCOMING.colorClass}`}
                            >
                              {incomingQty}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : product.qty_available !== undefined ? (
                <div className="flex items-center gap-1 bg-white bg-opacity-80 px-1 py-0.5 rounded text-gray-800">
                  <div
                    className={`w-3 h-3 rounded-sm ${getInventoryColorClass(product.qty_available)} ${product.qty_available > 0 && product.qty_available <= 5 ? "animate-pulse" : ""}`}
                    title={getInventoryStatusText(product.qty_available)}
                  ></div>
                  <span className="text-xs font-medium">
                    {product.qty_available}
                  </span>
                </div>
              ) : null}
            </span>
            <span className="text-xs">{product.xref_code}</span>
            <span className="text-xs">{product.product_code}</span>
            <span className="text-xs text-gray-500">
              Weight: {product.shipping_weight} lbs
            </span>
            <span className="text-xs text-gray-500">
              Serial Breaks: {product.serial_breaks || "N/A"}
            </span>
            <span className="text-xs text-gray-500">
              OEM Part#s: {product.oem_part_numbers || "N/A"}
            </span>
            {alternative && (
              <div className="flex items-center gap-2 mt-1 rounded-md bg-amber-50 border-l-4 border-amber-400 px-2 py-1">
                <p className="text-xs text-amber-800">
                  {alternative.description} ({alternative.product_code}) is
                  in stock ({alternative.available_qty} available)
                </p>
                <button
                  type="button"
                  onClick={handleSearchAlternative}
                  className="rounded-md bg-[var(--primary-color)] px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-[var(--secondary-color)] whitespace-nowrap"
                >
                  Search This Item
                </button>
              </div>
            )}
            <WhatDoesThisFit product={product} />
          </div>
        </td>

        <td className="px-3 text-sm text-gray-700 flex flex-col lg:py-2  lg:table-cell">
          <div className="flex flex-col">
            <span>
              {product.price === 0 || !product.price
                ? "-"
                : formatMoney(product.price)}
            </span>
            {product.tariff_amount > 0 && (
              <span className="text-xs text-gray-500">
                incl {formatMoney(product.tariff_amount)} tariff
              </span>
            )}
          </div>
        </td>

        <td className="px-3  text-sm text-gray-700 flex flex-col lg:py-2 lg:table-cell">
          {product.date_ordered}
        </td>

        <td className="px-3  text-sm text-gray-700 flex flex-col lg:py-2 lg:table-cell text-end">
          {!updateCarts ? (
            <></>
          ) : cartItem ? (
            <div className="flex flex-col items-end">
              <CartItemCounter cartItem={cartItem} />
              {product.price > 0 && (
                <span className="text-xs text-gray-500 mt-1">
                  {formatMoney(
                    getLineTotal(
                      cartItem.last_price,
                      cartItem.quantity,
                      cartItem.tariff_amount,
                    ),
                  )}{" "}
                  total
                </span>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-end">
              <button
                type="button"
                onClick={handleAddCartItem}
                className="rounded-lg bg-[var(--primary-color)] px-2 py-1 text-sm font-semibold text-white shadow-xs hover:bg-[var(--secondary-color)] mt-1"
              >
                Add to Cart
              </button>
            </div>
          )}
        </td>
      </tr>

      {/* Image Modal */}
      {showImageModal && product.image_name && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={handleModalClick}
        >
          <div className="relative max-w-full max-h-full p-4">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-800 z-10"
            >
              ×
            </button>
            <img
              src={
                process.env.REACT_APP_IMAGE_URL +
                convertImageNameToWeb(product.image_name)
              }
              alt={product.description}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}

      <BackorderConfirmModal
        show={showBackorderConfirm}
        onConfirm={handleConfirmBackorder}
        onCancel={() => setShowBackorderConfirm(false)}
      />
    </>
  );
}
